import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  CheckCircle,
  Database,
  FileText,
  Loader2,
  Play,
  Plus,
  RefreshCw,
  Settings,
  Trash2,
  Workflow,
  XCircle,
  Zap,
} from 'lucide-react';
import { api } from '../../lib/api';
import { Select } from '../../components/ui';

type Connection = {
  id: string;
  name: string;
  supabase_url: string;
  supabase_key: string;
  bucket: string;
  output_bucket?: string;
  ocr_mode: string;
  llm_enabled: boolean;
  llm_url?: string;
  llm_model?: string;
  form_template_id?: string;
  trigger_mode: string;
  poll_interval_min?: number;
  enabled: boolean;
  last_run?: string;
};

type FormTemplate = {
  id: string;
  name: string;
  description: string;
  system_prompt: string;
};

type PipelineJob = {
  job_key: string;
  status: string;
  started: string;
  path?: string;
  bucket?: string;
  ocr_elapsed?: number;
  llm_elapsed?: number;
  error?: string;
};

const inp =
  'w-full rounded-xl border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-blue-500';
const btn =
  'inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-50';

const TABS = ['Napojení', 'Šablony', 'Historie'] as const;
type Tab = typeof TABS[number];

// Hodnoty TABS řídí stav (logika) — překládá se jen zobrazení přes tuto mapu.
const TAB_KEYS: Record<Tab, string> = {
  'Napojení': 'pipeline.tabConnections',
  'Šablony': 'pipeline.tabTemplates',
  'Historie': 'pipeline.tabHistory',
};

export default function PipelinePage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>('Napojení');

  // Connections + forms CRUD/list + jobs: MCP (pipeline.connections / forms / list).
  const connectionsQ = useQuery<{ connections: Connection[] }>({
    queryKey: ['pipeline-connections'],
    queryFn: () =>
      api.mcp<{ connections: Connection[] }>('pipeline.connections', { action: 'list' }),
  });

  const formsQ = useQuery<{ templates: FormTemplate[] }>({
    queryKey: ['pipeline-forms'],
    queryFn: () =>
      api.mcp<{ templates: FormTemplate[] }>('pipeline.forms', { action: 'list' }),
  });

  const jobsQ = useQuery<{ jobs: PipelineJob[] }>({
    queryKey: ['pipeline-jobs'],
    queryFn: async () => {
      const r = await api.mcp<{ ok: boolean; recent_jobs?: PipelineJob[] }>(
        'pipeline.list',
        {},
      );
      return { jobs: r.recent_jobs ?? [] };
    },
    refetchInterval: tab === 'Historie' ? 5000 : false,
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['pipeline-connections'] });
    qc.invalidateQueries({ queryKey: ['pipeline-jobs'] });
  };

  return (
    <div className='space-y-5'>
      <div className='flex items-center justify-between'>
        <div className='flex items-center gap-3'>
          <div className='w-9 h-9 rounded-xl bg-orange-500/10 border border-orange-500/20 flex items-center justify-center'>
            <Workflow size={16} className='text-orange-400' />
          </div>
          <div>
            <h1 className='text-white font-semibold'>Document Pipeline</h1>
            <p className='text-xs text-gray-500'>{t('pipeline.subtitle')}</p>
          </div>
        </div>
        <button
          onClick={refresh}
          className='p-2 text-gray-600 hover:text-white hover:bg-gray-800 rounded-lg'
        >
          <RefreshCw size={13} />
        </button>
      </div>

      {/* Tabs */}
      <div className='flex gap-1 border-b border-gray-800'>
        {TABS.map((tabName) => (
          <button
            key={tabName}
            onClick={() => setTab(tabName)}
            className={`px-4 py-2 text-xs rounded-t-lg border-b-2 -mb-px transition-colors ${
              tab === tabName
                ? 'text-blue-400 border-blue-500 bg-blue-500/5'
                : 'text-gray-500 border-transparent hover:text-white'
            }`}
          >
            {t(TAB_KEYS[tabName])}
          </button>
        ))}
      </div>

      {tab === 'Napojení' && (
        <ConnectionsTab
          connections={connectionsQ.data?.connections ?? []}
          forms={formsQ.data?.templates ?? []}
          onRefresh={refresh}
        />
      )}
      {tab === 'Šablony' && (
        <FormsTab
          forms={formsQ.data?.templates ?? []}
          onRefresh={() => qc.invalidateQueries({ queryKey: ['pipeline-forms'] })}
        />
      )}
      {tab === 'Historie' && <JobsTab jobs={jobsQ.data?.jobs ?? []} isLoading={jobsQ.isFetching} />}
    </div>
  );
}

// ── Connections tab ───────────────────────────────────────────────────────────

function ConnectionsTab({ connections, forms, onRefresh }: {
  connections: Connection[];
  forms: FormTemplate[];
  onRefresh: () => void;
}) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState<Partial<Connection> | null>(null);

  const saveMut = useMutation({
    mutationFn: (conn: Partial<Connection>) =>
      api.mcp('pipeline.connections', { action: 'upsert', ...conn }),
    onSuccess: () => {
      toast.success(t('pipeline.savedToast'));
      setEditing(null);
      onRefresh();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) =>
      api.mcp('pipeline.connections', { action: 'delete', id }),
    onSuccess: () => {
      toast.success(t('pipeline.deletedToast'));
      onRefresh();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const pollMut = useMutation({
    mutationFn: (id: string) =>
      api.mcp<{ triggered: number; errors: number }>('pipeline.poll', {
        connection_id: id,
      }),
    onSuccess: (r) =>
      toast.success(t('pipeline.pollResultToast', { triggered: r.triggered, errors: r.errors })),
    onError: (e: Error) => toast.error(e.message),
  });

  const triggerMut = useMutation({
    mutationFn: ({ id, path }: { id: string; path: string }) =>
      api.mcp('pipeline.trigger', { connection_id: id, path }),
    onSuccess: () => toast.success(t('pipeline.pipelineStartedToast')),
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className='space-y-4'>
      <button
        onClick={() => setEditing({})}
        className={`${btn} bg-blue-600 text-white hover:bg-blue-500`}
      >
        <Plus size={13} /> {t('pipeline.newConnection')}
      </button>

      {connections.length === 0 && !editing && (
        <div className='text-center py-12 text-gray-600'>
          <Database size={32} className='mx-auto mb-3 opacity-30' />
          <p className='text-sm'>{t('pipeline.noConnections')}</p>
        </div>
      )}

      {connections.map((conn) => (
        <ConnectionCard
          key={conn.id}
          conn={conn}
          forms={forms}
          onEdit={() => setEditing(conn)}
          onDelete={() => deleteMut.mutate(conn.id)}
          onPoll={() => pollMut.mutate(conn.id)}
          onTrigger={(path) => triggerMut.mutate({ id: conn.id, path })}
          isPending={deleteMut.isPending || pollMut.isPending}
        />
      ))}

      {editing !== null && (
        <ConnectionForm
          initial={editing}
          forms={forms}
          onSave={saveMut.mutate}
          onCancel={() => setEditing(null)}
          isPending={saveMut.isPending}
        />
      )}
    </div>
  );
}

function ConnectionCard({ conn, forms, onEdit, onDelete, onPoll, onTrigger, isPending }: {
  conn: Connection;
  forms: FormTemplate[];
  onEdit: () => void;
  onDelete: () => void;
  onPoll: () => void;
  onTrigger: (path: string) => void;
  isPending: boolean;
}) {
  const { t } = useTranslation();
  const [triggerPath, setTriggerPath] = useState('');
  const tpl = forms.find((f) => f.id === conn.form_template_id);

  return (
    <div
      className={`rounded-2xl border p-4 space-y-3 ${
        conn.enabled
          ? 'border-gray-800 bg-gray-900/30'
          : 'border-gray-800/50 bg-gray-900/10 opacity-60'
      }`}
    >
      <div className='flex items-center justify-between'>
        <div className='flex items-center gap-3'>
          <Database size={14} className='text-blue-400' />
          <div>
            <div className='text-sm font-medium text-white'>{conn.name}</div>
            <div className='text-xs text-gray-500'>
              {conn.bucket} · {conn.ocr_mode} · {conn.trigger_mode}
            </div>
          </div>
          {tpl && (
            <span className='text-xs px-2 py-0.5 rounded-full bg-orange-500/10 border border-orange-500/20 text-orange-400'>
              {tpl.name}
            </span>
          )}
        </div>
        <div className='flex gap-2'>
          {conn.trigger_mode === 'poll' && (
            <button
              onClick={onPoll}
              disabled={isPending}
              className={`${btn} border border-gray-700 text-gray-400 hover:text-white hover:bg-gray-800`}
            >
              <Zap size={12} /> {t('pipeline.pollButton')}
            </button>
          )}
          <button
            onClick={onEdit}
            className={`${btn} border border-gray-700 text-gray-400 hover:text-white hover:bg-gray-800`}
          >
            <Settings size={12} />
          </button>
          <button
            onClick={onDelete}
            disabled={isPending}
            className={`${btn} border border-red-900/50 text-red-400 hover:bg-red-500/10`}
          >
            <Trash2 size={12} />
          </button>
        </div>
      </div>

      {/* Ruční trigger */}
      <div className='flex gap-2'>
        <input
          className={`${inp} flex-1 text-xs`}
          value={triggerPath}
          onChange={(e) => setTriggerPath(e.target.value)}
          placeholder={t('pipeline.triggerPathPlaceholder')}
        />
        <button
          onClick={() => {
            if (triggerPath) {
              onTrigger(triggerPath);
              setTriggerPath('');
            }
          }}
          disabled={!triggerPath || isPending}
          className={`${btn} bg-orange-600/20 border border-orange-500/30 text-orange-400 hover:bg-orange-600/30`}
        >
          <Play size={12} /> {t('pipeline.runButton')}
        </button>
      </div>
    </div>
  );
}

function ConnectionForm({ initial, forms, onSave, onCancel, isPending }: {
  initial: Partial<Connection>;
  forms: FormTemplate[];
  onSave: (conn: Partial<Connection>) => void;
  onCancel: () => void;
  isPending: boolean;
}) {
  const { t } = useTranslation();
  const [f, setF] = useState<Partial<Connection>>({
    ocr_mode: 'fast',
    trigger_mode: 'webhook',
    llm_enabled: true,
    llm_url: 'https://10.118.200.18:8004',
    llm_model: 'qwen3-coder-30b',
    enabled: true,
    poll_interval_min: 30,
    ...initial,
  });

  return (
    <div className='rounded-2xl border border-blue-500/20 bg-blue-500/5 p-5 space-y-4'>
      <h3 className='text-sm font-medium text-white'>
        {f.id ? t('pipeline.editConnection') : t('pipeline.newConnection')}
      </h3>

      <div className='grid grid-cols-2 gap-3'>
        <label className='block'>
          <div className='text-xs text-gray-500 mb-1'>{t('pipeline.nameLabel')}</div>
          <input
            className={inp}
            value={f.name ?? ''}
            onChange={(e) => setF((p) => ({ ...p, name: e.target.value }))}
            placeholder={t('pipeline.namePlaceholder')}
          />
        </label>
        <label className='block'>
          <div className='text-xs text-gray-500 mb-1'>{t('pipeline.bucketLabel')}</div>
          <input
            className={inp}
            value={f.bucket ?? ''}
            onChange={(e) => setF((p) => ({ ...p, bucket: e.target.value }))}
            placeholder='Investoroom'
          />
        </label>
      </div>

      <label className='block'>
        <div className='text-xs text-gray-500 mb-1'>{t('pipeline.supabaseUrlLabel')}</div>
        <input
          className={inp}
          value={f.supabase_url ?? ''}
          onChange={(e) => setF((p) => ({ ...p, supabase_url: e.target.value }))}
          placeholder='https://supabase1.services.maxcloud.cz'
        />
      </label>

      <label className='block'>
        <div className='text-xs text-gray-500 mb-1'>{t('pipeline.serviceRoleKeyLabel')}</div>
        <input
          className={inp}
          type='password'
          value={f.supabase_key ?? ''}
          onChange={(e) => setF((p) => ({ ...p, supabase_key: e.target.value }))}
          placeholder='eyJ...'
        />
      </label>

      <div className='grid grid-cols-3 gap-3'>
        <label className='block'>
          <div className='text-xs text-gray-500 mb-1'>{t('pipeline.ocrModeLabel')}</div>
          <Select
            value={f.ocr_mode}
            onChange={(e) =>
              setF((p) => ({ ...p, ocr_mode: e.target.value as 'fast' | 'quality' }))}
          >
            <option value='fast'>fast (GOT-OCR2.0)</option>
            <option value='quality'>quality</option>
          </Select>
        </label>
        <label className='block'>
          <div className='text-xs text-gray-500 mb-1'>{t('pipeline.triggerLabel')}</div>
          <Select
            value={f.trigger_mode}
            onChange={(e) => setF((p) => ({ ...p, trigger_mode: e.target.value }))}
          >
            <option value='webhook'>{t('pipeline.triggerWebhookOption')}</option>
            <option value='poll'>{t('pipeline.triggerPollingOption')}</option>
            <option value='disabled'>{t('pipeline.triggerDisabledOption')}</option>
          </Select>
        </label>
        {f.trigger_mode === 'poll' && (
          <label className='block'>
            <div className='text-xs text-gray-500 mb-1'>{t('pipeline.intervalMinLabel')}</div>
            <input
              className={inp}
              type='number'
              value={f.poll_interval_min ?? 30}
              onChange={(e) => setF((p) => ({ ...p, poll_interval_min: Number(e.target.value) }))}
            />
          </label>
        )}
      </div>

      {/* LLM */}
      <div className='space-y-3 border-t border-gray-800 pt-3'>
        <label className='flex items-center gap-2 cursor-pointer'>
          <input
            type='checkbox'
            checked={f.llm_enabled ?? true}
            onChange={(e) => setF((p) => ({ ...p, llm_enabled: e.target.checked }))}
          />
          <span className='text-sm text-gray-300'>{t('pipeline.llmStructuringLabel')}</span>
        </label>
        {f.llm_enabled && (
          <>
            <div className='grid grid-cols-2 gap-3'>
              <label className='block'>
                <div className='text-xs text-gray-500 mb-1'>{t('pipeline.llmUrlLabel')}</div>
                <input
                  className={inp}
                  value={f.llm_url ?? ''}
                  onChange={(e) => setF((p) => ({ ...p, llm_url: e.target.value }))}
                />
              </label>
              <label className='block'>
                <div className='text-xs text-gray-500 mb-1'>{t('pipeline.modelLabel')}</div>
                <input
                  className={inp}
                  value={f.llm_model ?? ''}
                  onChange={(e) => setF((p) => ({ ...p, llm_model: e.target.value }))}
                />
              </label>
            </div>
            <label className='block'>
              <div className='text-xs text-gray-500 mb-1'>{t('pipeline.formTemplateLabel')}</div>
              <Select
                value={f.form_template_id ?? ''}
                onChange={(e) =>
                  setF((p) => ({ ...p, form_template_id: e.target.value || undefined }))}
              >
                <option value=''>{t('pipeline.noTemplateOption')}</option>
                {forms.map((tpl) => <option key={tpl.id} value={tpl.id}>{tpl.name}</option>)}
              </Select>
            </label>
          </>
        )}
      </div>

      <div className='flex gap-2 pt-2'>
        <button
          onClick={() => onSave(f)}
          disabled={isPending || !f.name || !f.supabase_url || !f.bucket}
          className={`${btn} bg-blue-600 text-white hover:bg-blue-500`}
        >
          {isPending ? <Loader2 size={12} className='animate-spin' /> : null} {t('common.save')}
        </button>
        <button
          onClick={onCancel}
          className={`${btn} border border-gray-700 text-gray-400 hover:text-white hover:bg-gray-800`}
        >
          {t('common.cancel')}
        </button>
      </div>
    </div>
  );
}

// ── Forms tab ─────────────────────────────────────────────────────────────────

function FormsTab({ forms, onRefresh }: { forms: FormTemplate[]; onRefresh: () => void }) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState<Partial<FormTemplate> | null>(null);

  const saveMut = useMutation({
    mutationFn: (tpl: Partial<FormTemplate>) =>
      api.mcp('pipeline.forms', { action: 'upsert', ...tpl }),
    onSuccess: () => {
      toast.success(t('pipeline.templateSavedToast'));
      setEditing(null);
      onRefresh();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) =>
      api.mcp('pipeline.forms', { action: 'delete', id }),
    onSuccess: () => {
      toast.success(t('pipeline.deletedToast'));
      onRefresh();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const BUILTIN = ['invoice', 'contract', 'receipt', 'id_document', 'custom'];

  return (
    <div className='space-y-3'>
      <button
        onClick={() => setEditing({ id: '_new_' + Date.now(), name: '', system_prompt: '' })}
        className={`${btn} bg-blue-600 text-white hover:bg-blue-500`}
      >
        <Plus size={13} /> {t('pipeline.customTemplateButton')}
      </button>

      <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
        {forms.map((tpl) => (
          <div
            key={tpl.id}
            className='rounded-xl border border-gray-800 bg-gray-900/30 p-4 space-y-2'
          >
            <div className='flex items-center justify-between'>
              <div className='flex items-center gap-2'>
                <FileText size={13} className='text-orange-400' />
                <span className='text-sm font-medium text-white'>{tpl.name}</span>
                {BUILTIN.includes(tpl.id) && (
                  <span className='text-[10px] text-gray-600 border border-gray-700 rounded px-1'>
                    {t('pipeline.builtinBadge')}
                  </span>
                )}
              </div>
              {!BUILTIN.includes(tpl.id) && (
                <div className='flex gap-1'>
                  <button
                    onClick={() => setEditing(tpl)}
                    className='p-1.5 text-gray-600 hover:text-white hover:bg-gray-800 rounded-lg'
                  >
                    <Settings size={11} />
                  </button>
                  <button
                    onClick={() => deleteMut.mutate(tpl.id)}
                    className='p-1.5 text-gray-600 hover:text-red-400 hover:bg-red-500/10 rounded-lg'
                  >
                    <Trash2 size={11} />
                  </button>
                </div>
              )}
            </div>
            <p className='text-xs text-gray-500'>{tpl.description}</p>
          </div>
        ))}
      </div>

      {editing && (
        <div className='rounded-2xl border border-blue-500/20 bg-blue-500/5 p-5 space-y-3'>
          <h3 className='text-sm font-medium text-white'>{t('pipeline.formTemplateLabel')}</h3>
          <label className='block'>
            <div className='text-xs text-gray-500 mb-1'>{t('pipeline.nameLabel')}</div>
            <input
              className={inp}
              value={editing.name ?? ''}
              onChange={(e) => setEditing((p) => ({ ...p!, name: e.target.value }))}
            />
          </label>
          <label className='block'>
            <div className='text-xs text-gray-500 mb-1'>{t('pipeline.descriptionLabel')}</div>
            <input
              className={inp}
              value={editing.description ?? ''}
              onChange={(e) => setEditing((p) => ({ ...p!, description: e.target.value }))}
            />
          </label>
          <label className='block'>
            <div className='text-xs text-gray-500 mb-1'>{t('pipeline.llmSystemPromptLabel')}</div>
            <textarea
              className={`${inp} min-h-[160px] font-mono text-xs`}
              value={editing.system_prompt ?? ''}
              onChange={(e) => setEditing((p) => ({ ...p!, system_prompt: e.target.value }))}
            />
          </label>
          <div className='flex gap-2'>
            <button
              onClick={() => saveMut.mutate(editing)}
              disabled={saveMut.isPending || !editing.name}
              className={`${btn} bg-blue-600 text-white hover:bg-blue-500`}
            >
              {saveMut.isPending ? <Loader2 size={12} className='animate-spin' /> : null} {t('common.save')}
            </button>
            <button
              onClick={() => setEditing(null)}
              className={`${btn} border border-gray-700 text-gray-400 hover:text-white hover:bg-gray-800`}
            >
              {t('common.cancel')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Jobs tab ──────────────────────────────────────────────────────────────────

function JobsTab({ jobs, isLoading }: { jobs: PipelineJob[]; isLoading: boolean }) {
  const { t } = useTranslation();
  return (
    <div className='space-y-2'>
      {isLoading && jobs.length === 0 && (
        <div className='flex items-center gap-2 text-gray-600 py-8 justify-center'>
          <Loader2 size={14} className='animate-spin' />
          <span className='text-sm'>{t('pipeline.loadingJobs')}</span>
        </div>
      )}
      {jobs.length === 0 && !isLoading && (
        <div className='text-center py-12 text-gray-600 text-sm'>{t('pipeline.noJobs')}</div>
      )}
      {jobs.map((job) => (
        <div
          key={job.job_key}
          className='rounded-xl border border-gray-800 bg-gray-950/40 px-4 py-3'
        >
          <div className='flex items-center gap-3'>
            {job.status === 'done'
              ? <CheckCircle size={13} className='text-emerald-400 shrink-0' />
              : job.status === 'error'
              ? <XCircle size={13} className='text-red-400 shrink-0' />
              : <Loader2 size={13} className='text-blue-400 animate-spin shrink-0' />}
            <div className='flex-1 min-w-0'>
              <div className='text-xs text-white font-mono truncate'>{job.path ?? job.job_key}</div>
              <div className='text-[10px] text-gray-600'>
                {job.bucket} · {new Date(job.started).toLocaleString('cs-CZ')}
                {job.ocr_elapsed && ` · OCR ${job.ocr_elapsed}s`}
                {job.llm_elapsed && ` · LLM ${job.llm_elapsed}s`}
              </div>
              {job.error && <div className='text-[10px] text-red-400 mt-0.5'>{job.error}</div>}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
